import * as React from 'react';
import { useCallback, useState } from 'react';

/** Material UI Packages. */
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { readSocketData } from 'shared/lib/fetchSocket';
import { StyledPopper, formLabelTypographySx, formAutocompleteSx, rateManagementFilterLabelSx, rateManagementFilterAutocompleteSx } from '../lib/rateManagementShared';

export function TradePartnerAutocomplete({ label, fieldKey, value, onChange, dispatch, clientError, compact }) {
    const [options, setOptions] = useState([]);

    const fetchOptions = useCallback(
        (search) => {
            readSocketData(dispatch, null, 'cell', 'il_trade_partner', fieldKey, search || '', (received) =>
                setOptions(Array.isArray(received) ? received : []),
            );
        },
        [dispatch, fieldKey],
    );

    const labelSx = compact ? rateManagementFilterLabelSx : formLabelTypographySx;
    const autocompleteSx = compact ? rateManagementFilterAutocompleteSx : formAutocompleteSx;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <Typography sx={labelSx} title={label}>{label}</Typography>
            <Autocomplete
                size="small"
                value={value}
                onChange={(e, v) => onChange(v)}
                onInputChange={(e, v, reason) => {
                    if (reason === 'input') fetchOptions(v);
                }}
                onFocus={() => fetchOptions('')}
                options={options}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                slotProps={{ popper: { component: StyledPopper } }}
                sx={autocompleteSx}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={Boolean(clientError)}
                        helperText={clientError ? clientError : undefined}
                        label=""
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: '0 0 5px 5px' }}>
                        {option.name}
                    </li>
                )}
            />
        </Box>
    );
}

/**
 * `tab`은 백엔드 `Read.il_location`의 ocean/air 분기 그대로 전달한다 — `tab in [0, 1, 4]`는 ocean(POL/POD),
 * 그 외(2, 3)는 air(AOL/AOD)를 조회한다 (websocket/system/src/read/read.py). 호출부에서 필드 성격에 맞는 값을 넘겨야 한다.
 */
export function LocationCodeNameAutocomplete({ label, value, onChange, dispatch, compact, tab = 0 }) {
    const [options, setOptions] = useState([]);

    const fetchOptions = useCallback(
        (search) => {
            readSocketData(dispatch, tab, 'cell', 'il_location', '', search || '', (received) =>
                setOptions(Array.isArray(received) ? received : []),
            );
        },
        [dispatch, tab],
    );

    const labelSx = compact ? rateManagementFilterLabelSx : formLabelTypographySx;
    const autocompleteSx = compact ? rateManagementFilterAutocompleteSx : formAutocompleteSx;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <Typography sx={labelSx} title={label}>{label}</Typography>
            <Autocomplete
                size="small"
                value={value}
                onChange={(e, v) => onChange(v)}
                onInputChange={(e, v, reason) => {
                    if (reason === 'input') fetchOptions(v);
                }}
                onFocus={() => fetchOptions('')}
                options={options}
                getOptionLabel={(o) => (o?.code && o?.name ? `${o.code} | ${o.name}` : '')}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                slotProps={{ popper: { component: StyledPopper } }}
                sx={autocompleteSx}
                renderInput={(params) => <TextField {...params} label="" />}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: '0 0 5px 5px' }}>
                        {option.code} | {option.name}
                    </li>
                )}
            />
        </Box>
    );
}

/**
 * Shipments rate (`UpdateRateInfoTable` / `il_rate_type`)와 동일 소스·표시: `read` + entity `il_rate_type`, 옵션 `{ id, name }`.
 */
export function RateTypeAutocomplete({ label, value, onChange, dispatch, compact }) {
    const [options, setOptions] = useState([]);

    const fetchOptions = useCallback(
        (search) => {
            readSocketData(dispatch, 0, 'cell', 'il_rate_type', '', search || '', (received) =>
                setOptions(Array.isArray(received) ? received : []),
            );
        },
        [dispatch],
    );

    const labelSx = compact ? rateManagementFilterLabelSx : formLabelTypographySx;
    const autocompleteSx = compact ? rateManagementFilterAutocompleteSx : formAutocompleteSx;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <Typography sx={labelSx} title={label}>{label}</Typography>
            <Autocomplete
                size="small"
                value={value}
                onChange={(e, v) => onChange(v)}
                onInputChange={(e, v, reason) => {
                    if (reason === 'input') fetchOptions(v);
                }}
                onFocus={() => fetchOptions('')}
                options={options}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                slotProps={{ popper: { component: StyledPopper } }}
                sx={autocompleteSx}
                renderInput={(params) => <TextField {...params} label="" />}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: '0 0 5px 5px' }}>
                        {option.name}
                    </li>
                )}
            />
        </Box>
    );
}

/**
 * `il_team` read 응답을 Team 필터 옵션으로 정리한다 — 운영 중(status !== false)인 팀만 남긴다.
 * 레거시 응답처럼 status가 없는 팀은 활성으로 본다.
 */
export function normalizeActiveTeamOptions(data) {
    const list = Array.isArray(data) ? data : Array.isArray(data?.il_team) ? data.il_team : [];
    return list
        .filter((t) => t && typeof t === 'object' && t.id != null && t.status !== false)
        .map((t) => ({ id: t.id, name: String(t.name ?? '') }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

export function TeamAutocomplete({ label, value, onChange, dispatch, compact }) {
    const [options, setOptions] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const fetchOptions = useCallback(() => {
        if (loaded) return;
        setLoaded(true);
        readSocketData(dispatch, null, 'row', 'il_team', null, null, (received) =>
            setOptions(normalizeActiveTeamOptions(received)),
        );
    }, [dispatch, loaded]);

    const labelSx = compact ? rateManagementFilterLabelSx : formLabelTypographySx;
    const autocompleteSx = compact ? rateManagementFilterAutocompleteSx : formAutocompleteSx;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <Typography sx={labelSx} title={label}>{label}</Typography>
            <Autocomplete
                size="small"
                value={value}
                onChange={(e, v) => onChange(v)}
                onFocus={fetchOptions}
                options={options}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                slotProps={{ popper: { component: StyledPopper } }}
                sx={autocompleteSx}
                renderInput={(params) => <TextField {...params} label="" />}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: '0 0 5px 5px' }}>
                        {option.name}
                    </li>
                )}
            />
        </Box>
    );
}
